import { useState } from 'react'
import { OpalApiResponseDocument } from '@opallabs/api'
import {
  kuiperClassNames,
  Form,
  RadioArray,
  Select,
  DateRangePicker,
} from '@opallabs/kuiper'
import {
  AutoExpandTextArea,
  InputLabel,
  TextInputWithLabel,
} from '@opallabs/lapidary'
import { TransformContent } from '@opallabs/periodic'
import { testFeature } from '@opallabs/principles'
import { actions } from '@opallabs/wormhole'
import { Brand, StoryDuplicationData, StoryDuplicatorFormProps } from '../types'
import * as styles from './styles'
import './styles.scss'

export type promptProps = {
  type: string
  prompt: string
}

export const StoryDuplicatorForm: React.FC<StoryDuplicatorFormProps> = ({
  brandId,
  brands,
  description,
  duplicatedFromStoryId,
  name,
  onClose,
  onSubmit,
  scheduledEndAt,
  scheduledStartAt,
}) => {
  const [duplicatedDescription, setDuplicatedDescription] = useState<string>(
    description || ''
  )
  const [duplicatedName, setDuplicatedName] = useState<string>(name || '')
  const [duplicatedScheduledEndAt, setDuplicatedScheduledEndAt] =
    useState<Date>(scheduledEndAt)
  const [duplicatedScheduledStartAt, setDuplicatedScheduledStartAt] =
    useState<Date>(scheduledStartAt)
  const [duplicationOption, setDuplicationOption] = useState<string>('all')
  const [duplicateToBrandId, setDuplicateToBrandId] = useState<number>(brandId)
  const [submitText, setSubmitText] = useState<string>('Duplicate')
  const [prompt, setPrompt] = useState<promptProps>({ type: '', prompt: '' })

  const submitDisabled = () => {
    return duplicatedName.length === 0
  }

  const getBrandOption = () => {
    return brands.find((b) => b.id === duplicateToBrandId)
  }

  const submit = () => {
    if (submitDisabled()) return

    const postParams = {
      brandId,
      description: duplicatedDescription,
      duplicatedFromStoryId,
      duplicateToBrandId,
      duplicationOption,
      name: duplicatedName,
      prompt: prompt.prompt,
      promptType: prompt.type,
      scheduledEndAt: duplicatedScheduledEndAt,
      scheduledStartAt: duplicatedScheduledStartAt,
    }

    setSubmitText('Duplicating...')
    actions
      .createStory({ story: postParams })
      .then((response) => handleSubmission(response.data))
  }

  const handleSubmission = (
    resp: OpalApiResponseDocument<'CreateStoryV2', 201>
  ) => {
    const data: StoryDuplicationData = {
      ...resp.data,
      duplicationOption,
      duplicateToBrandId,
    }

    onSubmit(data)
    onClose()
  }

  const duplicationOptions = [
    { label: 'Include moments and full content', value: 'all' },
    {
      label: 'Include moments and content placeholders',
      value: 'placeholders_only',
    },
    { label: 'Include only moments', value: 'moments_only' },
    { label: 'Only duplicate story', value: 'story_only' },
  ]

  return (
    <Form
      cancelText="Cancel"
      customClass={kuiperClassNames('story-duplicator-form')}
      disableSubmit={submitDisabled()}
      onCancel={onClose}
      onSubmit={submit}
      submitText={submitText}
      title="Duplicate Story"
    >
      <div css={styles.formSection}>
        <InputLabel required={false} text="Duplication Options" theme="dark" />
        <RadioArray
          name="duplication-options"
          options={duplicationOptions}
          onChange={setDuplicationOption}
          value={duplicationOption}
        />
      </div>
      {brands.length > 1 ? (
        <div css={styles.formSection}>
          <InputLabel
            required={false}
            text="Duplicate to Workspace"
            theme="dark"
          />
          <Select
            searchable={true}
            theme="dark"
            onSelect={(option: Brand): void => setDuplicateToBrandId(option.id)}
            options={brands}
            role="listbox"
            value={getBrandOption()}
            placeholder={'Select a Workspace'}
          />
        </div>
      ) : null}
      <div css={styles.formSection}>
        <TextInputWithLabel
          initialValue={duplicatedName}
          labelText="Story Name"
          onChange={(value: string): void => setDuplicatedName(value)}
          placeholder="Story name"
          required={true}
          styleOverrides={{ label: styles.label, inputContainer: styles.input }}
          theme="light"
        />
      </div>
      <div css={styles.formSection}>
        <InputLabel required={false} text="Story Description" theme="dark" />
        <AutoExpandTextArea
          css={styles.description}
          placeholder="The description of this story"
          onChange={(textarea): void =>
            setDuplicatedDescription(textarea.target.value)
          }
          theme="light"
          minRows={2}
          maxRows={4}
          required={false}
          value={duplicatedDescription}
        />
      </div>
      <div css={styles.formSection}>
        <InputLabel required={false} text="Story Date Range" theme="dark" />
        <DateRangePicker
          endDate={duplicatedScheduledEndAt}
          onChangeEndDate={(value) => setDuplicatedScheduledEndAt(value)}
          onChangeStartDate={(value) => setDuplicatedScheduledStartAt(value)}
          startDate={duplicatedScheduledStartAt}
          v2Popover={true}
        />
      </div>
      {testFeature('ai_features') ? (
        <div css={styles.formSection}>
          <div
            css={styles.contentVariation}
            className={kuiperClassNames('content-variations')}
          >
            <TransformContent onSetPrompt={setPrompt} theme="light" />
          </div>
        </div>
      ) : null}
    </Form>
  )
}
